import React, { useState } from 'react';
import { apiService } from '../services/api';

interface AllLeaguesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJoinLeague: (league: any) => void;
  leagues: any[];
  loading: boolean;
  error: string | null;
}

const AllLeaguesModal: React.FC<AllLeaguesModalProps> = ({ 
  isOpen, 
  onClose, 
  onJoinLeague,
  leagues,
  loading,
  error
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');

  if (!isOpen) return null;

  const filteredLeagues = leagues.filter(league => {
    const matchesSearch = league.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         league.tournament_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || 
                       league.league_type === selectedType || 
                       league.tournament_type === selectedType || 
                       league.type === selectedType;
    return matchesSearch && matchesType;
  });

  const getLeagueTypeInfo = (type: string) => {
    switch (type) {
      case 'pickem':
        return { label: 'Pick\'em', icon: '🎯', color: 'bg-purple-600' };
      case 'budget_league':
        return { label: 'Budget League', icon: '💰', color: 'bg-green-600' };
      case 'draft_league':
        return { label: 'Draft League', icon: '📋', color: 'bg-blue-600' };
      case 'h2h':
        return { label: 'H2H', icon: '⚔️', color: 'bg-red-600' };
      case 'eliminator':
        return { label: 'Eliminator', icon: '🏆', color: 'bg-yellow-600' };
      default:
        return { label: type, icon: '🏏', color: 'bg-gray-600' };
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Join a League</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-gray-700 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Search Leagues</label>
              <input
                type="text"
                placeholder="Search by league name or tournament..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">League Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="pickem">Pick'em</option>
                <option value="budget_league">Budget League</option>
                <option value="draft_league">Draft League</option>
                <option value="h2h">H2H</option>
                <option value="eliminator">Eliminator</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-400 text-xl mb-2">⚠️</div>
              <p className="text-white mb-4">{error}</p>
            </div>
          ) : filteredLeagues.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏏</div>
              <p className="text-xl text-white mb-2">No leagues found</p>
              <p className="text-blue-200">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-4">
                              {filteredLeagues.map(league => {
                const leagueType = league.league_type || league.tournament_type || league.type;
                const typeInfo = getLeagueTypeInfo(leagueType);
                return (
                  <div key={league.id} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                      {/* Left side - League info */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="text-2xl">{typeInfo.icon}</span>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{league.name}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color} text-white`}>
                                {typeInfo.label}
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-600 text-white">
                                {league.is_public ? 'Public' : 'Private'}
                              </span>
                              <span className="text-gray-400 text-sm">
                                {league.tournament_name}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-white/60">Participants:</span>
                            <div className="text-white font-medium">{league.current_participants}/{league.max_participants}</div>
                          </div>
                          <div>
                            <span className="text-white/60">Status:</span>
                            <div className="text-white font-medium">{league.status}</div>
                          </div>
                          <div>
                            <span className="text-white/60">Created:</span>
                            <div className="text-white font-medium">{new Date(league.created_at).toLocaleDateString()}</div>
                          </div>
                          <div>
                            <span className="text-white/60">Progress:</span>
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-600 rounded-full h-2">
                                <div
                                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${(league.current_participants / league.max_participants) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-white/60">
                                {Math.round((league.current_participants / league.max_participants) * 100)}%
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Show warning if league is almost full */}
                        {league.current_participants >= league.max_participants * 0.9 && league.current_participants < league.max_participants && (
                          <div className="mt-3 p-2 bg-yellow-900 bg-opacity-20 border border-yellow-500 text-yellow-300 rounded text-xs">
                            ⚠️ Only {league.max_participants - league.current_participants} spots remaining!
                          </div>
                        )}
                      </div>
                      
                      {/* Right side - Action buttons */}
                      <div className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2">
                        <button
                          onClick={() => onJoinLeague(league)}
                          disabled={league.current_participants >= league.max_participants}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            league.current_participants >= league.max_participants
                              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {league.current_participants >= league.max_participants ? 'League Full' : 'Join League'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-600">
          <div className="flex justify-between items-center">
            <p className="text-white/60 text-sm">
              Showing {filteredLeagues.length} of {leagues.length} leagues
            </p>
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllLeaguesModal; 